import type { Account } from "@/context/FinanceContext";
import { PersistedState } from "./useFinanceState";
import { toast } from "@/hooks/use-toast";

export function useAccountsOperations(
  state: PersistedState,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  user: any,
  createAccount: (data: any) => Promise<string>,
  updateSupabaseAccount: (id: string, data: any) => Promise<boolean>,
  deleteAccount: (id: string) => Promise<boolean>
) {
  const addAccount = async (name: string, color: string = "#64748B", initialBalance: number = 0, extra?: { agencia?: string; contaCorrente?: string }): Promise<string | null> => {
    // Check for duplicate names
    const existingAccount = state.accounts.find(a => a.name.toLowerCase() === name.toLowerCase());
    if (existingAccount) {
      toast({
        variant: "destructive",
        title: "Nome duplicado",
        description: "Já existe uma conta com este nome.",
      });
      return null;
    }

    const tempId = `acc-${crypto.randomUUID()}`;
    
    // Sync to Supabase if user is logged in
    if (user) {
      try {
        const supabaseId = await createAccount({ name, color, initial_balance: initialBalance, agencia: extra?.agencia || null, conta_corrente: extra?.contaCorrente || null });
        if (supabaseId) {
          // Update local state with the real ID from Supabase
          setState((s) => ({
            ...s,
            accounts: [...s.accounts, { id: supabaseId, name, color, initialBalance, agencia: extra?.agencia, contaCorrente: extra?.contaCorrente }],
          }));
          return supabaseId;
        }
      } catch (error) {
        console.error('Error syncing account to Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível salvar a conta na nuvem.",
        });
        return null;
      }
    } else {
      // Update local state with temp ID if not logged in
      setState((s) => ({
        ...s,
        accounts: [...s.accounts, { id: tempId, name, color, initialBalance, agencia: extra?.agencia, contaCorrente: extra?.contaCorrente }],
      }));
      return tempId;
    }
    return null;
  };

  const updateAccount = async (id: string, name?: string, color?: string, initialBalance?: number, extra?: { agencia?: string; contaCorrente?: string }) => {
    // Update local state first
    setState((s) => ({
      ...s,
      accounts: s.accounts.map((a) => (
        a.id === id ? { ...a, ...(name !== undefined ? { name } : {}), ...(color !== undefined ? { color } : {}), ...(initialBalance !== undefined ? { initialBalance } : {}), ...(extra?.agencia !== undefined ? { agencia: extra.agencia } : {}), ...(extra?.contaCorrente !== undefined ? { contaCorrente: extra.contaCorrente } : {}) } : a
      )),
    }));

    // Sync to Supabase if user is logged in
    if (user) {
      try {
        await updateSupabaseAccount(id, {
          ...(name && { name }),
          ...(color && { color }),
          ...(initialBalance !== undefined && { initial_balance: initialBalance }),
          ...(extra?.agencia !== undefined && { agencia: extra.agencia || null }),
          ...(extra?.contaCorrente !== undefined && { conta_corrente: extra.contaCorrente || null }),
        });
      } catch (error) {
        console.error('Error syncing account update to Supabase:', error);
      }
    }
  };

  const removeAccount = async (id: string) => {
    // Sync to Supabase if user is logged in first
    if (user) {
      try {
        const success = await deleteAccount(id);
        if (!success) {
          // Se a exclusão no Supabase falhou, não remove do estado local
          return;
        }
        
        toast({
          title: "Conta excluída",
          description: "A conta foi removida com sucesso.",
        });
      } catch (error) {
        console.error('Error deleting account from Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível excluir a conta. Tente novamente.",
        });
        return;
      }
    }

    // Update local state only after successful Supabase deletion (or if offline)
    setState((s) => ({
      ...s,
      accounts: s.accounts.filter((a) => a.id !== id),
    }));

    if (!user) {
      toast({
        title: "Conta excluída localmente",
        description: "A conta foi removida localmente. Faça login para sincronizar.",
      });
    }
  };

  return {
    addAccount,
    updateAccount,
    removeAccount,
  };
}
